import { HttpEndpointForm } from '@/components/admin/http-endpoints/HttpEndpointForm';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import type {
  CreateHttpEndpointRequest,
  TestHttpEndpointResponse,
  UpdateHttpEndpointRequest,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';

interface AdminHttpEndpointDetailProps {
  id: string;
}

export function AdminHttpEndpointDetail({ id }: AdminHttpEndpointDetailProps) {
  const isNew = id === 'new';
  return isNew ? <CreateView /> : <EditView id={id} />;
}

function BackLink() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate({ to: '/admin/http-endpoints' })}
      className="mb-4 inline-flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" />
      사내 API Endpoint 목록
    </button>
  );
}

function CreateView() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: CreateHttpEndpointRequest) => api.adminHttpEndpointCreate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'http-endpoints'] });
      navigate({ to: '/admin/http-endpoints' });
    },
  });

  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? 'Endpoint 생성에 실패했습니다.'
        : null;

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title="새 Endpoint"
            description="Skill 의 http.* 툴이 호출할 수 있는 외부 REST API 를 등록합니다."
          />
        </>
      }
    >
      <HttpEndpointForm
        mode="create"
        submitLabel="Endpoint 만들기"
        submitting={mutation.isPending}
        serverError={serverError}
        onSubmit={(body) => mutation.mutate(body as CreateHttpEndpointRequest)}
        onCancel={() => navigate({ to: '/admin/http-endpoints' })}
      />
    </PageShell>
  );
}

function EditView({ id }: { id: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [testResult, setTestResult] = useState<TestHttpEndpointResponse | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'http-endpoints', id],
    queryFn: () => api.adminHttpEndpointGet(id),
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateHttpEndpointRequest) => api.adminHttpEndpointUpdate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'http-endpoints'] });
      qc.invalidateQueries({ queryKey: ['admin', 'http-endpoints', id] });
      navigate({ to: '/admin/http-endpoints' });
    },
  });

  const testMutation = useMutation({
    mutationFn: () => api.adminHttpEndpointTest(id),
    onSuccess: (r) => setTestResult(r),
    onError: () =>
      setTestResult({
        ok: false,
        status: null,
        error: '요청에 실패했습니다.',
        tokenPreview: null,
        latencyMs: 0,
      }),
  });

  if (query.isLoading) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-foreground-muted">불러오는 중…</p>
      </PageShell>
    );
  }
  if (query.error || !query.data) {
    return (
      <PageShell header={<BackLink />}>
        <p className="text-sm text-destructive">
          {query.error instanceof ApiError ? query.error.message : 'Endpoint 를 찾을 수 없습니다.'}
        </p>
      </PageShell>
    );
  }

  const ep = query.data;
  const serverError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? '수정에 실패했습니다.'
        : null;

  return (
    <PageShell
      header={
        <>
          <BackLink />
          <PageHeader
            title={ep.name}
            description={ep.baseUrl}
            actions={
              <Button
                type="button"
                variant="ghost"
                disabled={testMutation.isPending}
                onClick={() => {
                  setTestResult(null);
                  testMutation.mutate();
                }}
              >
                {testMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                연결 테스트
              </Button>
            }
          />
        </>
      }
    >
      {testResult ? <TestResultBanner result={testResult} /> : null}
      <HttpEndpointForm
        mode="edit"
        initial={{
          name: ep.name,
          baseUrl: ep.baseUrl,
          description: ep.description,
          authType: ep.authType,
          basicUsername: ep.basicUsername,
          loginUsername: ep.loginUsername,
          loginConfig: ep.loginConfig,
          cookieMode: ep.cookieMode,
          allowedMethods: ep.allowedMethods,
          pathAllowPatterns: ep.pathAllowPatterns,
          pathDenyPatterns: ep.pathDenyPatterns,
          defaultHeaders: ep.defaultHeaders,
          requestTimeoutMs: ep.requestTimeoutMs,
          maxResponseBytes: ep.maxResponseBytes,
          hasBearerToken: ep.hasBearerToken,
          hasBasicPassword: ep.hasBasicPassword,
          hasLoginPassword: ep.hasLoginPassword,
        }}
        submitLabel="저장"
        submitting={updateMutation.isPending}
        serverError={serverError}
        onSubmit={(body) => updateMutation.mutate(body as UpdateHttpEndpointRequest)}
        onCancel={() => navigate({ to: '/admin/http-endpoints' })}
      />
    </PageShell>
  );
}

function TestResultBanner({ result }: { result: TestHttpEndpointResponse }) {
  if (result.ok) {
    return (
      <div className="mb-4 flex items-start gap-2 rounded-md border border-[hsl(150_60%_45%)]/30 bg-[hsl(150_60%_45%)]/5 px-3 py-2 text-sm text-[hsl(150_60%_45%)]">
        <CheckCircle2 className="size-4 mt-0.5" />
        <div>
          연결 성공{result.status ? ` · status ${result.status}` : ''} · {result.latencyMs}ms
          {result.tokenPreview ? ` · token ${result.tokenPreview}` : ''}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      <XCircle className="size-4 mt-0.5" />
      <div>
        연결 실패
        {result.status ? ` · status ${result.status}` : ''}
        {result.error ? ` — ${result.error}` : ''}
      </div>
    </div>
  );
}
